import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import {
  Card,
  CardContent,
  EmptyState,
  ErrorBanner,
  PageHeader,
} from '@saas-kit/ui';
import type { PaginationState } from '@saas-kit/ui/DataTable';

import { listAuditLogs } from './api';
import { AuditFilters, type AuditFilterValues } from './AuditFilters';
import { AuditTable } from './AuditTable';

const DEFAULT_PAGE_SIZE = 50;

const FILTER_KEYS = ['tenant_id', 'actor_id', 'action'] as const;

export function AuditPage(): JSX.Element {
  const { t } = useTranslation('adminAudit');
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo<AuditFilterValues>(
    () => ({
      tenant_id: searchParams.get('tenant_id') ?? '',
      actor_id: searchParams.get('actor_id') ?? '',
      action: searchParams.get('action') ?? '',
    }),
    [searchParams],
  );

  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const queryParams = {
    offset,
    limit: pageSize,
    tenant_id: filters.tenant_id || null,
    actor_id: filters.actor_id || null,
    action: filters.action || null,
  };

  const logsQuery = useQuery({
    queryKey: ['admin-audit-logs', queryParams],
    queryFn: () => listAuditLogs(queryParams),
    placeholderData: (prev) => prev,
  });

  function applyFilters(next: AuditFilterValues): void {
    const params = new URLSearchParams(searchParams);
    for (const key of FILTER_KEYS) {
      const value = next[key];
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    }
    setSearchParams(params, { replace: true });
    setOffset(0);
  }

  function clearFilters(): void {
    const params = new URLSearchParams(searchParams);
    for (const key of FILTER_KEYS) {
      params.delete(key);
    }
    setSearchParams(params, { replace: true });
    setOffset(0);
  }

  const logs = logsQuery.data?.items ?? [];
  const page = logsQuery.data?.page;
  const total = page?.total ?? 0;
  const hasNext = page ? page.offset + page.limit < page.total : false;
  const hasPrev = offset > 0;

  const pagination: PaginationState = {
    pageIndex: Math.floor(offset / pageSize),
    pageSize,
    hasNext,
    hasPrev,
    onNext: () => setOffset(offset + pageSize),
    onPrev: () => setOffset(Math.max(0, offset - pageSize)),
    onPageSizeChange: (n) => {
      setPageSize(n);
      setOffset(0);
    },
  };

  const hasActiveFilters =
    Boolean(filters.tenant_id) ||
    Boolean(filters.actor_id) ||
    Boolean(filters.action);

  const showEmpty =
    !logsQuery.isLoading &&
    !logsQuery.isError &&
    total === 0 &&
    !hasActiveFilters;

  return (
    <div className="space-y-6">
      <PageHeader title={t('title')} description={t('subtitle')} />

      <AuditFilters
        values={filters}
        onApply={applyFilters}
        onClear={clearFilters}
      />

      {logsQuery.isError ? (
        <ErrorBanner>{t('errors.load_failed')}</ErrorBanner>
      ) : null}

      {showEmpty ? (
        <Card>
          <CardContent className="p-0">
            <EmptyState title={t('empty.title')} description={t('empty.body')} />
          </CardContent>
        </Card>
      ) : (
        <AuditTable
          logs={logs}
          pagination={pagination}
          isLoading={logsQuery.isLoading}
          error={logsQuery.isError ? t('errors.load_failed') : null}
        />
      )}
    </div>
  );
}
